import { useEffect, useRef, useState } from 'react';

/**
 * Browser-back interception layer (finding 35: internal navigation — drill
 * stacks, inline panels, edit forms — was invisible to browser history, so
 * the browser "Atgal" skipped it entirely and fell through the raw history
 * stack to the login-era entry, killing the session and silently losing
 * unsaved work).
 *
 * Complements useAppBackFloor (Layout) which stops Back from exiting the
 * APP boundary; this layer makes Back drive INTERNAL navigation instead:
 *
 *   useAppBack({ enabled: depth > 0, goBack: () => { pop(); return more; } })
 *   useAppBack({ enabled: true, blocking: dirty })            // edit routes
 *
 * HOW A PRESS IS INTERCEPTED (sentinel strategy)
 *
 * While a consumer would actually intercept (it consumes levels, or is
 * dirty-blocking) the hook ARMS a "sentinel": a history entry pushed on top
 * of the current one with the SAME URL and an `{ __appBack: true }` state
 * marker. A browser back press then pops the sentinel instead of leaving
 * the page — same URL, so react-router's popstate listener sees an
 * unchanged location and no-ops (verified: the router only reacts to
 * popstate; pushState fires none).
 *
 * On popstate (single global listener, installed once at module load —
 * before the router mounts, so it also runs first):
 *   - top consumer is dirty-blocking → re-push the sentinel (undo the
 *     press), open the unsaved-changes confirm (AppBackConfirmHost):
 *       · „Likti"  → nothing (the re-pushed sentinel stays armed);
 *       · „Išeiti" → custom onConfirmLeave (e.g. close the form — the
 *         unregister cleanup pops the sentinel) or, for route-level
 *         guards, unregister + history.go(-2) (skip the page entry and the
 *         re-pushed sentinel), guarded by an ignore-flag so the handler
 *         does not re-intercept its own correction.
 *   - top consumer can consume → call goBack(); if it reports more levels
 *     remain, re-push the sentinel for the next press. If it reports the
 *     last level, do NOT re-push — the press itself consumed the sentinel,
 *     so the browser is already back at the page entry (no orphan).
 *   - no consumer / a passive consumer (clean edit route) → do nothing;
 *     the router performs normal navigation.
 *
 * ORPHAN CONTROL: when a consumer unregisters while its sentinel is still
 * the current entry (user closed the panel via the internal button, not
 * back), the cleanup pops it with history.back() — but ONLY when the
 * current entry is still provably ours: it carries the marker, OR no
 * history entry was pushed since we armed (a pushState-only URL REPLACE,
 * e.g. the analytics page syncing ?view= into the URL, wipes the marker
 * but keeps the position — still ours). A router PUSH in between means the
 * entry is no longer ours; popping then would drag the user off the page
 * they navigated to. Every programmatic back() sets `ignoreNextPopstate`
 * so the handler ignores the resulting popstate exactly once — that flag
 * is what makes the whole thing loop-safe.
 *
 * STRICTMODE: double-mounted effects produce an extra forward sentinel
 * entry in dev (push → queued back → push); exactly one armed entry at the
 * current position remains, which is all the logic relies on. Prod is
 * unaffected. Registration itself is idempotent per hook instance (the
 * cleanup removes exactly its own registry entry).
 *
 * jsdom/tests: the handler reacts to any popstate event, so tests dispatch
 * `window.dispatchEvent(new PopstateEvent('popstate'))` to simulate a
 * press; sentinel push/pop bookkeeping uses the real window.history.
 */

export interface AppBackConfig {
  /** Register only while true — e.g. `drillDepth > 0` / `formOpen`. */
  enabled: boolean;
  /**
   * Consume one back press (pop a drill level / close a panel). Returning
   * true means "I can consume further presses" (sentinel re-armed); false
   * means "that was my last level" (the press itself consumed the
   * sentinel — no re-arm, no orphan).
   */
  goBack?: () => boolean;
  /** While true, a back press opens the unsaved-changes confirm instead. */
  blocking?: boolean;
  /** Custom action after the user confirms leaving (default: navigate back). */
  onConfirmLeave?: () => void;
}

interface ConsumerEntry {
  canGoBack: () => boolean;
  goBack: () => boolean;
  isBlocking: () => boolean;
  customLeave: () => (() => void) | undefined;
  /** A sentinel we pushed is (probably) still the current history entry. */
  sentinelLive: boolean;
}

const SENTINEL_STATE = { __appBack: true } as const;

/** LIFO registry — the LAST registered consumer wins a back press. */
const consumers: ConsumerEntry[] = [];

/** True while our own programmatic history.back()/go() correction is in flight. */
let ignoreNextPopstate = false;

function pushSentinel() {
  // Same URL, marker state — react-router no-ops (it only listens to
  // popstate, and pushState fires none).
  window.history.pushState(SENTINEL_STATE, '', window.location.href);
}

function handlePopstate() {
  if (ignoreNextPopstate) {
    ignoreNextPopstate = false;
    return;
  }
  const top = consumers.at(-1);
  if (!top) return; // nothing registered → normal router navigation

  if (top.isBlocking()) {
    // Dirty: undo the press (re-push the popped sentinel) and ask the user.
    pushSentinel();
    top.sentinelLive = true;
    openConfirm(top);
    return;
  }
  if (!top.canGoBack()) return; // passive consumer (clean edit route) → let it through

  const stillActive = top.goBack();
  if (stillActive) {
    pushSentinel(); // re-arm for the next level
    top.sentinelLive = true;
  } else {
    // The press itself consumed the sentinel — the browser is already back
    // at the page entry. No re-arm, no orphan.
    top.sentinelLive = false;
  }
}

// Installed ONCE at module load (this module is imported by pages before
// BrowserRouter mounts, so the handler also runs ahead of the router's own
// popstate listener). Idempotent by construction — StrictMode-proof.
window.addEventListener('popstate', handlePopstate);

// --- Unsaved-changes confirm store (rendered by AppBackConfirmHost) --------

interface ConfirmState {
  consumer: ConsumerEntry;
}
let confirmState: ConfirmState | null = null;
const confirmSubscribers = new Set<() => void>();

function notifyConfirm() {
  for (const fn of confirmSubscribers) fn();
}

function openConfirm(consumer: ConsumerEntry) {
  confirmState = { consumer };
  notifyConfirm();
}

function closeConfirm() {
  confirmState = null;
  notifyConfirm();
}

/** Test/inspection access (also used by AppBackConfirmHost). */
export function isConfirmOpen(): boolean {
  return confirmState !== null;
}

export function subscribeConfirm(cb: () => void): () => void {
  confirmSubscribers.add(cb);
  return () => confirmSubscribers.delete(cb);
}

function confirmLeave() {
  const consumer = confirmState?.consumer;
  closeConfirm();
  if (!consumer) return;
  const custom = consumer.customLeave();
  if (custom) {
    custom(); // e.g. close the form — the enabled-flip cleanup pops the sentinel
    return;
  }
  // Route-level guard: unregister, then jump back past the re-pushed
  // sentinel AND the page entry (history: [.., prev, page, sentinel*]).
  // history.go clamps at the first entry, so a directly-opened edit page
  // with no previous entry simply stays (the tab close is the exit there).
  consumer.sentinelLive = false; // the go(-2) consumes the sentinel
  const idx = consumers.indexOf(consumer);
  if (idx !== -1) consumers.splice(idx, 1);
  ignoreNextPopstate = true;
  window.history.go(-2);
}

// --- The hook ----------------------------------------------------------------

export function useAppBack(config: AppBackConfig) {
  // Latest-ref: the registry holds stable delegators, the config stays fresh.
  const ref = useRef(config);
  ref.current = config;
  // The registry entry — shared between the registration and sentinel
  // effects (registration runs first, it owns the entry's lifecycle).
  const entryRef = useRef<ConsumerEntry | null>(null);

  const { enabled } = config;
  const blocking = !!config.blocking;
  const consumes = config.goBack != null;

  // Registration (LIFO membership) — re-runs only when `enabled` flips.
  useEffect(() => {
    if (!enabled) return;
    const entry: ConsumerEntry = {
      canGoBack: () => ref.current.goBack != null,
      goBack: () => ref.current.goBack?.() ?? false,
      isBlocking: () => !!ref.current.blocking,
      customLeave: () => ref.current.onConfirmLeave,
      sentinelLive: false,
    };
    entryRef.current = entry;
    consumers.push(entry);
    return () => {
      const idx = consumers.indexOf(entry);
      if (idx !== -1) consumers.splice(idx, 1);
      if (entryRef.current === entry) entryRef.current = null;
      if (confirmState?.consumer === entry) closeConfirm();
    };
  }, [enabled]);

  // Sentinel arming is SPLIT so a dirty-flip never churns history (a pop+push
  // pair races its own in-flight popstate). Consumers that CONSUME levels
  // (drill stacks, panels) arm while enabled — clean or dirty alike;
  // route-level guards (no goBack) arm only while dirty, so a clean form's
  // back navigates natively.

  const popIfStillOurs = (armedAtLength: number) => {
    const entry = entryRef.current;
    if (!entry?.sentinelLive) return;
    // Pop only when the current entry is still provably ours: the marker,
    // or nothing was PUSHED since arming (a URL replace — e.g. ?view=
    // syncing — wipes the marker but keeps the position ours).
    const stillOurs =
      (window.history.state as { __appBack?: boolean } | null)?.__appBack ||
      window.history.length === armedAtLength;
    if (stillOurs) {
      ignoreNextPopstate = true;
      window.history.back();
    }
    entry.sentinelLive = false;
  };

  // Arm A — consuming consumers (drill/panel): armed while enabled.
  useEffect(() => {
    if (!enabled || !consumes) return;
    const armedAtLength = window.history.length;
    pushSentinel();
    if (entryRef.current) entryRef.current.sentinelLive = true;
    return () => popIfStillOurs(armedAtLength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, consumes]);

  // Arm B — route guards (no goBack): armed only while dirty.
  useEffect(() => {
    if (!enabled || consumes || !blocking) return;
    const armedAtLength = window.history.length;
    pushSentinel();
    if (entryRef.current) entryRef.current.sentinelLive = true;
    return () => popIfStillOurs(armedAtLength);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, consumes, blocking]);
}

// --- Refresh / tab-close guard ----------------------------------------------

/**
 * Generic refresh/tab-close protection while dirty. The browser shows its
 * own message; the handler stays generic ("changes may be lost") — no
 * business data in it.
 */
export function useBeforeUnload(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy requirement: a non-undefined returnValue triggers the prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}

// --- Confirm host state bridge (component lives in components/) -------------

export function useConfirmState(): { open: boolean; onLeave: () => void; onStay: () => void } {
  const [open, setOpen] = useState(isConfirmOpen());
  useEffect(() => subscribeConfirm(() => setOpen(isConfirmOpen())), []);
  return { open, onLeave: confirmLeave, onStay: closeConfirm };
}
